import type { Pill } from '../SisMatching/sisMatchingView';
import type { FlagDistrict, FlagState, FlagView } from './featureFlagsApi';

export const LOAD_FAILED = 'Couldn’t load the feature flags. Refresh to try again.';

export const SAVE_FAILED = 'Couldn’t save. Try again.';

export const STAGE_LABELS: Record<string, string> = {
  in_design: 'In design',
  in_progress: 'In progress',
  ready: 'Ready',
  released: 'Released',
  retired: 'Retired',
};

export function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] ?? stage;
}

export function cardUrl(card: number): string {
  return `https://flashlightlearning.kanbanize.com/ctrl_board/10/cards/${card}/details/`;
}

export function prUrl(pr: number): string {
  return `https://github.com/hewett-learning/hewett-learning-app/pull/${pr}`;
}

export function isOn(state: FlagState | null): boolean {
  return state !== null && state.enabled && (state.scope === 'all' || state.districtIds.length > 0);
}

export function describeState(state: FlagState | null): string {
  if (!isOn(state) || state === null) return 'off';
  if (state.scope === 'all') return 'on for everyone';
  const n = state.districtIds.length;
  return `on for ${n} ${n === 1 ? 'district' : 'districts'}`;
}

export function flagPill(state: FlagState | null): Pill {
  if (!isOn(state) || state === null) return { tone: 'grey', label: 'Hidden' };
  if (state.scope === 'all') return { tone: 'green', label: 'Live' };
  const n = state.districtIds.length;
  return { tone: 'yellow', label: `${n} ${n === 1 ? 'district' : 'districts'}` };
}

function signature(state: FlagState | null): string {
  if (!isOn(state) || state === null) return 'off';
  if (state.scope === 'all') return 'all';
  return `districts:${[...state.districtIds].sort().join(',')}`;
}

export function releasePill(flags: FlagView[]): { pill: Pill; detail: string } | null {
  if (flags.length === 0) return null;
  const counts = new Map<string, number>();
  for (const f of flags) {
    const sig = signature(f.state);
    counts.set(sig, (counts.get(sig) ?? 0) + 1);
  }
  if (counts.size === 1) return { pill: flagPill(flags[0]!.state), detail: '' };
  let majority: string | null = null;
  for (const [sig, count] of counts) {
    if (count * 2 > flags.length) majority = sig;
  }
  const described = (f: FlagView) => `${f.key} ${describeState(f.state)}`;
  const detail = majority === null
    ? flags.map(described).join(' · ')
    : flags.filter((f) => signature(f.state) !== majority).map(described).join(' · ');
  return { pill: { tone: 'yellow', label: 'Partial' }, detail };
}

export function districtName(id: string, districts: FlagDistrict[]): string {
  const district = districts.find((d) => d.id === id);
  if (!district) return id;
  return district.state ? `${district.name} (${district.state})` : district.name;
}

export function districtNames(ids: string[], districts: FlagDistrict[]): string {
  return ids.map((id) => districtName(id, districts)).join(', ');
}

export function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' })} UTC`;
}

export function conflictMessage(current: FlagState | null): string {
  if (!current) return 'Someone changed this switch since the page loaded. Check the current state and confirm again.';
  return `Someone changed this switch at ${formatWhen(current.updatedAt)} (by ${current.updatedBy}). The row now shows that state; check it and confirm again.`;
}
